import { useQuery } from '@tanstack/react-query';
import api from '../../API';
import styles from './NetworkDiagram.module.css';

function NetworkDiagram() {
    // Query network description using public endpoint.
    // Backend automatically queries hub if we're a spoke, returns local data if we're a hub.
    const { data: networkResponse, isLoading, isError } = useQuery({
        queryKey: ['network-describe-public'],
        queryFn: () => api.get('network/describe/public'),
        staleTime: 2 * 60 * 1000, // 2 minutes - network data doesn't change frequently
        refetchOnMount: false, // Don't refetch if data is fresh
        refetchOnWindowFocus: false, // Don't refetch on window focus
    });

    const networkData = networkResponse?.data;
    
    // Show loading placeholder while data is being fetched
    if (isLoading) {
        return (
            <div className={styles.networkDiagram}>
                <h3>Girder Network</h3>
                <div className={styles.loadingPlaceholder}>
                    <div className={styles.loadingSpinner}></div>
                    <p>Loading network information...</p>
                </div>
            </div>
        );
    }
    
    // Show error state if query failed
    if (isError || !networkData) {
        return (
            <div className={styles.networkDiagram}>
                <h3>Girder Network</h3>
                <div className={styles.waitingMessage}>
                    Unable to load network information
                </div>
            </div>
        );
    }
    
    // Normalize current site UUID from response. Backend always includes the local node UUID
    // as localGirderNetworkUuid, but be defensive in case it is wrapped (e.g., as an array).
    let currentSiteUuid = networkData?.localGirderNetworkUuid ?? null;
    let networkUuid = networkData?.network?.girderNetworkUuid ?? null;
    let hubUuid = networkData?.hub?.girderNetworkUuid ?? null;
    // Determine if hub node is current site
    const hubIsCurrent = (networkUuid === currentSiteUuid) || (hubUuid === currentSiteUuid);
    
    const joinedGirderNetwork = !!networkData?.joinedGirderNetwork;
    const connectionError = !!networkData?.connectionError;

    // Case 1: Local node has not been set up.
    if (!currentSiteUuid) {
        return (
            <div className={styles.networkDiagram}>
                <h3>Girder Network</h3>
                <div className={styles.waitingMessage}>
                    Girder Network not configured. Please set up your local node in the Admin panel.
                </div>
            </div>
        );
    }

    // Case 2: Local node configured but not joined to any network.
    if (currentSiteUuid && !joinedGirderNetwork) {
        return (
            <div className={styles.networkDiagram}>
                <h3>Girder Network</h3>
                <div className={styles.waitingMessage}>
                    Local node configured but not connected to a network. Waiting to join a hub network.
                </div>
            </div>
        );
    }

    // Helper function to convert API URL to web URL
    // Helper function to convert API URL to web URL
    const getWebUrl = (apiUrl) => {
        if (!apiUrl) return null;
        // Remove /dsa/api/v1 if present, and trailing slashes
        let webUrl = apiUrl.replace('/dsa/api/v1', '').replace(/\/+$/, '');
        // Convert host.docker.internal to localhost for display
        webUrl = webUrl.replace(/host\.docker\.internal/g, 'localhost');
        // Ensure it has a protocol
        if (!webUrl.match(/^https?:\/\//)) {
            webUrl = `http://${webUrl}`;
        }
        return webUrl;
    };

    // Extract core structures from network description.
    const spokes = networkData?.spokes || [];

    // Case 3: Connection error talking to hub (spoke mode).
    // Render a minimal diagram: a grayed-out hub with the current spoke and two dummy spokes.
    let networkHub;
    let hubNode;
    let spokeNodes;
    let showConnectionErrorDiagram = false;

    if (connectionError) {
        // Find the current local spoke node, if present.
        const localSpoke = spokes.find(
            (spoke) => spoke.girderNetworkUuid === currentSiteUuid
        );

        if (!localSpoke) {
            // We can't reliably render a diagram without the local node.
            return (
                <div className={styles.networkDiagram}>
                    <h3>Girder Network</h3>
                    <div className={styles.waitingMessage}>
                        Unable to connect to hub site. Network information may be incomplete.
                    </div>
                </div>
            );
        }

        // Minimal diagram: synthetic hub node plus the local spoke.
        networkHub = {
            // Use the network name from the local node if available.
            networkName: localSpoke.networkName,
            displayName: localSpoke.networkName + '00', // we don't know the actual network identifier but it should end in 00
            apiUrl: null,
            girderNetworkUuid: null,
        };
        spokeNodes = [localSpoke];
        showConnectionErrorDiagram = true;
    } else {
        // Normal case: use hub node and all spokes from the description.
        networkHub = networkData?.network;
        hubNode = networkData?.hub;
        spokeNodes = spokes;
    }

    // If we still don't have a hub node and no spokes, there is nothing meaningful to show.
    if (!hubNode && spokeNodes.length === 0) {
        return (
            <div className={styles.networkDiagram}>
                <h3>Girder Network</h3>
                <div className={styles.waitingMessage}>
                    Waiting to connect to a Girder Network Hub
                </div>
            </div>
        );
    }

    // Get web URL for hub node
    const hubWebUrl = hubNode ? getWebUrl(hubNode.apiUrl) : null;


    // Determine the network name, always preferring the explicit networkName from networkInfo.
    let networkName = hubNode?.networkInfo?.networkName || hubNode?.networkName;
    if (!networkName) {
        // Try to recover networkName from a spoke that matches current site (e.g., after a connection error).
        const currentSpoke = spokeNodes.find(
            (spoke) => spoke.girderNetworkUuid === currentSiteUuid
        );
        networkName = currentSpoke?.networkInfo?.networkName || currentSpoke?.networkName || 'Girder';
    }

    // SVG dimensions
    const centerX = 250;
    const centerY = 250;
    const hubRadius = 40;
    const spokeDistance = 180;
    const spokeRadius = 30;

    // Calculate positions for spokes in a circle
    // Always show at least 3 positions (actual spokes + placeholders)
    const totalPositions = Math.max(3, spokeNodes.length);
    const spokePositions = spokeNodes.map((spoke, index) => {
        const angle = (index / totalPositions) * 2 * Math.PI - Math.PI / 2; // Start at top
        const x = centerX + spokeDistance * Math.cos(angle);
        const y = centerY + spokeDistance * Math.sin(angle);
        return { ...spoke, x, y, angle, isPlaceholder: false };
    });
    
    // Add placeholder positions if we have fewer than 3 spokes
    const placeholderCount = Math.max(0, 3 - spokeNodes.length);
    const placeholderPositions = [];
    for (let i = 0; i < placeholderCount; i++) {
        const index = spokeNodes.length + i;
        const angle = (index / totalPositions) * 2 * Math.PI - Math.PI / 2; // Start at top
        const x = centerX + spokeDistance * Math.cos(angle);
        const y = centerY + spokeDistance * Math.sin(angle);
        placeholderPositions.push({ x, y, angle, isPlaceholder: true });
    }
    
    // Combine actual spokes and placeholders for rendering
    const allSpokePositions = [...spokePositions, ...placeholderPositions];

    return (
        <div className={styles.networkDiagram}>
            <h3>{networkName} Network</h3>
            {showConnectionErrorDiagram && (
                <div className={styles.waitingMessage}>
                    Unable to connect to hub site. Network information may be incomplete.
                </div>
            )}
            <div className={styles.diagramContainer}>
                <svg width="500" height="500" viewBox="0 0 500 500" className={styles.diagramSvg}>
                    {/* Draw lines from hub to all spoke positions (including placeholders) */}
                    {hubNode && allSpokePositions.map((spoke, index) => (
                        <line
                            key={spoke.girderNetworkUuid ? `line-${spoke.girderNetworkUuid}` : `line-placeholder-${index}`}
                            x1={centerX}
                            y1={centerY}
                            x2={spoke.x}
                            y2={spoke.y}
                            className={spoke.isPlaceholder ? styles.placeholderLine : styles.connectionLine}
                        />
                    ))}

                    {/* Draw hub node */}
                    {hubNode && (
                        <g className={hubIsCurrent ? styles.currentNode : ''}>
                            {hubWebUrl && !hubIsCurrent ? (
                                <a href={hubWebUrl} target="_blank" rel="noopener noreferrer">
                                    <circle
                                        cx={centerX}
                                        cy={centerY}
                                        r={hubRadius}
                                        className={`${styles.node} ${styles.hubNode} ${styles.clickableNode}`}
                                        style={showConnectionErrorDiagram ? { opacity: 0.5 } : undefined}
                                    />
                                </a>
                            ) : (
                                <circle
                                    cx={centerX}
                                    cy={centerY}
                                    r={hubRadius}
                                    className={`${styles.node} ${styles.hubNode} ${hubIsCurrent ? styles.current : ''}`}
                                    style={showConnectionErrorDiagram ? { opacity: 0.5 } : undefined}
                                />
                            )}
                            {hubWebUrl && !hubIsCurrent ? (
                                <a href={hubWebUrl} target="_blank" rel="noopener noreferrer">
                                    <text
                                        x={centerX}
                                        y={centerY + hubRadius + 18}
                                        textAnchor="middle"
                                        className={`${styles.nodeName} ${styles.clickableText}`}
                                    >
                                        {networkName + ' Network Hub'}
                                    </text>
                                </a>
                            ) : (
                                <text
                                    x={centerX}
                                    y={centerY + hubRadius + 18}
                                    textAnchor="middle"
                                    className={styles.nodeName}
                                >
                                    {networkName + ' Network Hub'}
                                </text>
                            )}
                            <text
                                x={centerX}
                                y={centerY + hubRadius + 35}
                                textAnchor="middle"
                                className={styles.networkIdentifier}
                            >
                                {hubNode.displayName || 'Girder'}{hubNode.networkIdentifier ? ` (${hubNode.networkIdentifier})` : ''}
                            </text>
                            {hubIsCurrent && (
                                <text
                                    x={centerX}
                                    y={centerY + hubRadius + 52}
                                    textAnchor="middle"
                                    className={styles.currentLabel}
                                >
                                    (You are here)
                                </text>
                            )}
                        </g>
                    )}

                    {/* Draw actual spoke nodes */}
                    {spokePositions.map((spoke, index) => {
                        // Check if this spoke node matches the current site by UUID
                        const spokeUuid = spoke.girderNetworkUuid;
                        const spokeIsCurrent = spokeUuid === currentSiteUuid;
                        const displayName = spoke.displayName || `Spoke ${index + 1}`;
                        const spokeApiUrl = spoke.apiUrl;
                        const spokeWebUrl = getWebUrl(spokeApiUrl);

                        return (
                            <g key={`spoke-${spoke.girderNetworkUuid || index}`} className={spokeIsCurrent ? styles.currentNode : ''}>
                                {spokeWebUrl && !spokeIsCurrent ? (
                                    <a href={spokeWebUrl} target="_blank" rel="noopener noreferrer">
                                        <circle
                                            cx={spoke.x}
                                            cy={spoke.y}
                                            r={spokeRadius}
                                            className={`${styles.node} ${styles.spokeNode} ${styles.clickableNode}`}
                                        />
                                    </a>
                                ) : (
                                    <circle
                                        cx={spoke.x}
                                        cy={spoke.y}
                                        r={spokeRadius}
                                        className={`${styles.node} ${styles.spokeNode} ${spokeIsCurrent ? styles.current : ''}`}
                                    />
                                )}
                                {spokeWebUrl && !spokeIsCurrent ? (
                                    <a href={spokeWebUrl} target="_blank" rel="noopener noreferrer">
                                        <text
                                            x={spoke.x}
                                            y={spoke.y + spokeRadius + 18}
                                            textAnchor="middle"
                                            className={`${styles.nodeName} ${styles.clickableText}`}
                                        >
                                            {displayName}
                                        </text>
                                    </a>
                                ) : (
                                    <text
                                        x={spoke.x}
                                        y={spoke.y + spokeRadius + 18}
                                        textAnchor="middle"
                                        className={styles.nodeName}
                                    >
                                        {displayName}
                                    </text>
                                )}
                                {spoke.networkIdentifier && (
                                    <text
                                        x={spoke.x}
                                        y={spoke.y + spokeRadius + 35}
                                        textAnchor="middle"
                                        className={styles.networkIdentifier}
                                    >
                                        {spoke.networkIdentifier}
                                    </text>
                                )}
                                {spokeIsCurrent && (
                                    <text
                                        x={spoke.x}
                                        y={spoke.y + spokeRadius + (spoke.networkIdentifier ? 52 : 35)}
                                        textAnchor="middle"
                                        className={styles.currentLabel}
                                    >
                                        (You are here)
                                    </text>
                                )}
                            </g>
                        );
                    })}

                    {/* Draw placeholder nodes */}
                    {placeholderPositions.map((placeholder, index) => (
                        <g key={`placeholder-${index}`}>
                            <circle
                                cx={placeholder.x}
                                cy={placeholder.y}
                                r={spokeRadius}
                                className={`${styles.node} ${styles.placeholderNode}`}
                            />
                            <text
                                x={placeholder.x}
                                y={placeholder.y + spokeRadius + 18}
                                textAnchor="middle"
                                className={styles.placeholderText}
                            >
                                Coming Soon...
                            </text>
                        </g>
                    ))}
                </svg>
            </div>
        </div>
    );
}

export default NetworkDiagram;

